import { hexToRgb } from '../../../../../../shared/frontend/libs/utils';

export type TComparisonSeriesStyle = {
  color: string;
  dashStyle: string;
  dayDashStyle: string;
  markerSymbol: string;
  colorAdjusted: boolean;
  manualOverride: boolean;
};

const DEFAULT_COMPARISON_COLORS = [
  '#5ec8ff',
  '#ff8a3d',
  '#97d34f',
  '#ff5f7a',
  '#b785ff',
  '#ffd166',
  '#20c997',
  '#7aa5ff'
];

const SERIES_DASH_STYLES = ['Solid', 'ShortDash', 'ShortDot', 'ShortDashDot', 'LongDash', 'ShortDashDotDot'];
const SERIES_DAY_DASH_STYLES = ['LongDashDot', 'ShortDash', 'ShortDot', 'Dash', 'ShortDashDot', 'ShortDashDotDot'];
const SERIES_MARKER_SYMBOLS = ['circle', 'diamond', 'square', 'triangle', 'triangle-down'];
const MINIMUM_COLOR_DISTANCE = 72;

function getColorDistance(firstHex: string, secondHex: string) {
  const [r1, g1, b1] = hexToRgb(firstHex);
  const [r2, g2, b2] = hexToRgb(secondHex);

  return Math.sqrt(Math.pow(r1 - r2, 2) + Math.pow(g1 - g2, 2) + Math.pow(b1 - b2, 2));
}

function getMostDistinctPaletteColor(usedColors: string[], index: number) {
  if (!usedColors.length) {
    return DEFAULT_COMPARISON_COLORS[index % DEFAULT_COMPARISON_COLORS.length];
  }

  let bestColor = DEFAULT_COMPARISON_COLORS[index % DEFAULT_COMPARISON_COLORS.length];
  let bestDistance = -1;

  for (const paletteColor of DEFAULT_COMPARISON_COLORS) {
    const minDistance = Math.min(...usedColors.map((usedColor) => getColorDistance(paletteColor, usedColor)));

    if (minDistance > bestDistance) {
      bestDistance = minDistance;
      bestColor = paletteColor;
    }
  }

  return bestColor;
}

export function normalizeHexColor(color?: string | null) {
  if (!color) {
    return null;
  }

  const trimmed = color.trim();
  if (!trimmed) {
    return null;
  }

  const normalized = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  if (!/^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/.test(normalized)) {
    return null;
  }

  if (normalized.length === 4) {
    return `#${normalized[1]}${normalized[1]}${normalized[2]}${normalized[2]}${normalized[3]}${normalized[3]}`.toLowerCase();
  }

  return normalized.toLowerCase();
}

function parseManualSeriesColors(manualColorInput?: string | null) {
  if (!manualColorInput) {
    return [] as Array<string | null>;
  }

  return manualColorInput.split(',').map((color) => normalizeHexColor(color));
}

export function buildComparisonSeriesStyles(
  items: Array<{ color?: string | null }>,
  manualColorInput?: string | null
): TComparisonSeriesStyle[] {
  const manualColors = parseManualSeriesColors(manualColorInput);
  const usedColors: string[] = [];

  return items.map((item, index) => {
    const manualColor = manualColors[index] || null;
    const baseColor =
      manualColor ||
      normalizeHexColor(item.color) ||
      DEFAULT_COMPARISON_COLORS[index % DEFAULT_COMPARISON_COLORS.length];

    let color = baseColor;
    let colorAdjusted = false;

    if (!manualColor && usedColors.some((usedColor) => getColorDistance(usedColor, color) < MINIMUM_COLOR_DISTANCE)) {
      color = getMostDistinctPaletteColor(usedColors, index);
      colorAdjusted = color !== baseColor;
    }

    usedColors.push(color);

    return {
      color,
      dashStyle: SERIES_DASH_STYLES[index % SERIES_DASH_STYLES.length],
      dayDashStyle: SERIES_DAY_DASH_STYLES[index % SERIES_DAY_DASH_STYLES.length],
      markerSymbol: SERIES_MARKER_SYMBOLS[index % SERIES_MARKER_SYMBOLS.length],
      colorAdjusted,
      manualOverride: Boolean(manualColor)
    };
  });
}
